/**
 * Copyright (c) 2026, CheckPlus Limited
 */
import React, { Component } from "react";
import "./SelectPayment.css";
import PropTypes from "prop-types";
import CardItem from "./component/cardItem";
import { simpleFetch } from "../../restWrapper";
import { Helmet } from "react-helmet";
import { rememberOrgTitle } from "../../assets/orgTitle";
import { QRCodeCanvas } from "qrcode.react";
import checkplusLogo from "../../assets/component/images/checkplus-logo.svg";
import plugLogo from "../../assets/component/images/plug-logo.svg";
import { PLATFORM } from "../../config";
import {
  validatePlatformMatch,
  resolveThemeColors,
  isPaymentMethodAvailable,
  isPaymentMethodHiddenOnProject,
  organizationHasPayMe,
  organizationHasPayMeCredentials,
  PROJECT_CARD_KEY,
  PROJECT_WALLET_KEY,
} from "../../assets/helper";

import {
  PaymentRequestButtonElement,
} from "@stripe/react-stripe-js";
import { useStripe, useElements } from "@stripe/react-stripe-js";

class SelectPayment extends Component {
  constructor(props) {
    super(props);
    try {
      if (
        this.props.location.state === null ||
        this.props.location.state === undefined
      ) {
        this.props.history.push("/SelectMission");
      }
    } catch (e) {
      this.props.history.push("/SelectMission");
    }

    // Create a safe copy of location state with fallbacks for missing data
    const dataObj = this.props.location.state || {};

    // Ensure organization object exists with required properties
    if (!dataObj.organization) {
      dataObj.organization = {
        headerColor: "000000",
        buttonColor: "ffffff",
        organizationName: "Organization",
        logo: {},
        paymentMethodCode: {}
      };
    } else if (!dataObj.organization.paymentMethodCode) {
      dataObj.organization.paymentMethodCode = {};

      // Flag that we need to fetch payment methods
      dataObj.needToFetchPaymentMethods = true;
    }

    // Ensure mission object exists with required properties
    if (!dataObj.mission) {
      dataObj.mission = {
        title: "Payment",
        id: "unknown",
        status: "open"
      };
    }

    // Ensure amount is available
    const chargedAmount = dataObj.coverFee && dataObj.feeCoverageEstimate?.totalAmount
      ? dataObj.feeCoverageEstimate.totalAmount
      : (dataObj.amount || 0);
    const amount = Math.round(chargedAmount * 100);
    // console.log("Payment amount: " + amount);

    // Don't initialize paymentRequest in constructor - Stripe might not be loaded yet
    // Will be initialized in componentDidMount when stripe is available
    let paymentRequest = null;

    // Only initialize payment request if stripe is already available (rare)
    if (this.props.stripe) {
      paymentRequest = this.props.stripe.paymentRequest({
        country: "HK",
        currency: "hkd",
        total: {
          label: dataObj.mission ? dataObj.mission.title : "Payment",
          amount: amount,
        },
        requestPayerName: true,
        requestPayerEmail: true,
        requestPayerPhone: true,
      });

      paymentRequest.on(
        "paymentmethod",
        ({ complete, paymentMethod, ...data }) => {
          if (!paymentMethod) complete("fail");

          this.setState({
            paymentMethodId: paymentMethod.id,
            brand: paymentMethod.card.brand,
            name: paymentMethod.billing_details.name,
            email: paymentMethod.billing_details.email,
            phone: paymentMethod.billing_details.phone,
            wallet: paymentMethod.card.wallet.type,
            last4: paymentMethod.card.last4,
          });

          complete("success");
        }
      );

      paymentRequest.canMakePayment().then((result) => {
        this.setState({ canMakePayment: !!result });
      });
    }

    // Initialize state once with all properties
    this.state = {
      ...dataObj,
      canMakePayment: false,
      paymentRequest,
      fetchingOrganizationData: false,
      fetchError: null,
      fetchRetries: 0,
      maxRetries: 3
    };

    this.onBackBtnClicked = this.onBackBtnClicked.bind(this);
    this.onCardBtnClicked = this.onCardBtnClicked.bind(this);
    this.attachFeeState = this.attachFeeState.bind(this);
    this.initializePaymentRequest = this.initializePaymentRequest.bind(this);
    this.onScroll = this.onScroll.bind(this);
  }

  initializePaymentRequest() {
    // Only initialize if stripe is available and paymentRequest hasn't been created yet
    if (!this.props.stripe || this.state.paymentRequest) {
      return;
    }

    const chargedAmount = this.state.coverFee && this.state.feeCoverageEstimate?.totalAmount
      ? this.state.feeCoverageEstimate.totalAmount
      : (this.state.amount || 0);
    const amount = Math.round(chargedAmount * 100);

    const paymentRequest = this.props.stripe.paymentRequest({
      country: "HK",
      currency: "hkd",
      total: {
        label: this.state.mission ? this.state.mission.title : "Payment",
        amount: amount,
      },
      requestPayerName: true,
      requestPayerEmail: true,
      requestPayerPhone: true,
    });

    paymentRequest.on(
      "paymentmethod",
      ({ complete, paymentMethod, ...data }) => {
        if (!paymentMethod) complete("fail");

        this.setState({
          paymentMethodId: paymentMethod.id,
          brand: paymentMethod.card.brand,
          name: paymentMethod.billing_details.name,
          email: paymentMethod.billing_details.email,
          phone: paymentMethod.billing_details.phone,
          wallet: paymentMethod.card.wallet.type,
          last4: paymentMethod.card.last4,
        });

        complete("success");
      }
    );

    paymentRequest.canMakePayment().then((result) => {
      this.setState({
        canMakePayment: !!result,
        paymentRequest
      });
    }).catch((error) => {
      // console.error("Error checking payment request availability:", error);
      this.setState({ paymentRequest });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    // Initialize payment request when stripe becomes available
    if (!prevProps.stripe && this.props.stripe) {
      this.initializePaymentRequest();
    }

    // Check if authentication state changed
    const wasAuthenticated = prevProps.isAuth || prevState.isAuth || prevState.userAuthenticated;
    const isNowAuthenticated = this.props.isAuth || this.state.isAuth || this.state.userAuthenticated;

    // If user just became authenticated and we don't have cards yet, fetch them
    if (!wasAuthenticated && isNowAuthenticated && !this.state.cards) {
      // console.log("Authentication state changed to authenticated, fetching saved cards...");
      simpleFetch("/cards", "GET").then((res) => {
        // console.log("Cards fetch response status:", res.status);
        if (res.status !== 200) {
          // console.log("Failed to fetch cards, status:", res.status);
          return null;
        }
        // console.log("Saved cards fetched after auth change:", JSON.stringify(res.body));
        this.setState({
          cards: res.body,
        });
      }).catch((error) => {
        // console.log("Error fetching cards after auth change:", error);
      });
    }
  }

  componentDidMount() {
    window.scrollTo(0, 0);
    this.setState({
      isScrolling: false,
    });

    // Initialize payment request if stripe is now available
    this.initializePaymentRequest();

    // console.log("SelectPayment state: " + JSON.stringify(this.state) + " -85");
    // console.log("SelectPayment organization: " + JSON.stringify(this.state.organization) + " -86");
    // console.log("SelectPayment paymentMethod: " + JSON.stringify(this.state.paymentMethod) + " -87");
    
    // Ensure country is set, default to HK if not provided
    if (!this.state.country) {
      this.setState({ country: "HK" });
    }
    
    // Check for paymentMethodCode and fetch if missing
    if (!this.state.organization || !this.state.organization.paymentMethodCode) {
      // If paymentMethodCode is missing, try to fetch organization data directly as a fallback
      if (this.state.subdomain || this.state.domain) {
        this.fetchOrganizationData((this.state.isCustomDomain ? this.state.domain : this.state.subdomain), this.state.isCustomDomain);
      }
    }

    window.addEventListener("scroll", this.onScroll);

    // Check authentication from both props and location state
    const isAuthenticated = this.props.isAuth || this.state.isAuth || this.state.userAuthenticated;
    // console.log("SelectPayment authentication check - props.isAuth:", this.props.isAuth, "state.isAuth:", this.state.isAuth, "state.userAuthenticated:", this.state.userAuthenticated, "isAuthenticated:", isAuthenticated);

    if (isAuthenticated) {
      // console.log("User is authenticated, fetching saved cards...");
      simpleFetch("/cards", "GET").then((res) => {
        // console.log("Cards fetch response status:", res.status);
        if (res.status !== 200) {
          // console.log("Failed to fetch cards, status:", res.status);
          if (res.status === 401) {
            // console.log("Authentication failed, clearing auth state");
            // Clear authentication state if cards request fails with 401
            this.setState({
              isAuth: false,
              userAuthenticated: false
            });
          }
          return null;
        }
        // console.log("Saved cards fetched:", JSON.stringify(res.body));
        this.setState({
          cards: res.body,
        });
      }).catch((error) => {
        // console.error("Error fetching cards:", error);
      });
    } else {
      // console.log("User is not authenticated, not fetching saved cards");
      return null;
    }
  }

  onScroll() {
    const shouldBeScrolling = window.scrollY >= 100;
    // Only update state if the value actually changed to prevent unnecessary re-renders
    if (shouldBeScrolling !== this.state.isScrolling) {
      this.setState({ isScrolling: shouldBeScrolling });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("scroll", this.onScroll);
  }

  // Fetch organization data directly if it's missing (fallback mechanism)
  fetchOrganizationData(subdomain, isCustomDomain = false) {
    // Set loading state
    this.setState({ fetchingOrganizationData: true, fetchError: null });
    
    simpleFetch("/donors/organization", "GET", {
      subDomain: subdomain,
      isCustomDomain: isCustomDomain,
    }).then((res) => {
      if (res.status !== 200) {
        // console.error("Failed to fetch organization data:", res);
        this.setState({ 
          fetchingOrganizationData: false,
          fetchError: "Failed to fetch organization data"
        });
        return;
      }
      
      const orgObj = res.body.organizationObj;
      rememberOrgTitle(orgObj && orgObj.organizationName);
      // Validate platform matches organization type
      const platformMatch = validatePlatformMatch(
        orgObj.organizationType,
        PLATFORM
      );

      if (!platformMatch) {
        this.setState({
          fetchingOrganizationData: false,
          fetchError: "This page is not available on this platform"
        });
        return;
      }

      // Ensure paymentMethodCode exists
      if (!orgObj.paymentMethodCode) {
        orgObj.paymentMethodCode = {};
      }
      
      // Update the organization object with the fetched data
      this.setState({
        fetchingOrganizationData: false,
        fetchError: null,
        organization: {
          ...this.state.organization,
          paymentMethodCode: orgObj.paymentMethodCode,
          // Include organization ID
          organizationId: res.body.id,
          // Update other properties if needed
          currentPlan: orgObj.currentPlan || this.state.organization.currentPlan || "basic",
          // Whether PayMe is set up — reported instead of the credentials
          // (CP-326).
          paymeConfigured: orgObj.paymeConfigured === true,
          // Only an older backend still sends these; kept until CP-326 is
          // deployed so PayMe works whichever side ships first, then remove.
          clientId: orgObj.clientId,
          clientSecret: orgObj.clientSecret,
          signingKeyId: orgObj.signingKeyId,
          signingKey: orgObj.signingKey
        }
      });
    }).catch(error => {
      // console.error("Error fetching organization data:", error);
      this.setState({ 
        fetchingOrganizationData: false,
        fetchError: "Error fetching organization data: " + error.message
      });
    });
  }

  onBackBtnClicked() {
    let obj = {
      amount: this.props.location.state.amount,
      language: this.props.location.state.language,
      mission: this.props.location.state.mission,
      organization: this.props.location.state.organization,
      paymentMethod: this.props.location.state.paymentMethod,
      projectId: this.props.location.state.projectId,
      subdomain: this.props.location.state.subdomain,
      isCustomDomain: this.props.location.state.isCustomDomain,
      domain: this.props.location.state.domain,
      country: this.props.location.state.country,
      scheduledDate: this.props.location.state.scheduledDate,
      recurringOccurrences: this.props.location.state.recurringOccurrences,
      // FIX: Preserve all recurring payment fields when going back
      frequency: this.props.location.state.frequency,
      isInfiniteRecurring: this.props.location.state.isInfiniteRecurring,
      startDate: this.props.location.state.startDate,
    };

    if (this.state.donorInfo) obj.donorInfo = this.state.donorInfo;
    if (this.state.customFields) obj.customFields = this.state.customFields;

    // Preserve authentication state when going back
    if (this.props.isAuth || this.state.isAuth) {
      obj.isAuth = true;
      obj.userAuthenticated = true;  // Additional flag to indicate user has authenticated
      // console.log("Preserving authentication state when going back to PersonalDetails");
    }

    obj = this.attachFeeState(obj);

    this.props.history.push({ pathname: "/PersonalDetails", state: obj });
  }

  onCardBtnClicked() {
    // Remove sensitive fields before passing to CardPrompt
    const {
      registerInfo: _registerInfo,
      confirmPassword: _confirmPassword,
      confirmEmail: _confirmEmail,
      loginEmail: _loginEmail,
      loginPassword: _loginPassword,
      verificationCode: _verificationCode,
      createAccount: _createAccount,
      ...safeState
    } = this.props.location.state || {};

    this.props.history.push({
      pathname: "/CardPrompt",
      state: safeState,
    });
  }

  attachFeeState(base = {}) {
    const merged = { ...base };

    if (typeof this.state.coverFee === "boolean") {
      merged.coverFee = this.state.coverFee;
    } else if (
      merged.coverFee === undefined &&
      this.props.location.state &&
      this.props.location.state.coverFee !== undefined
    ) {
      merged.coverFee = this.props.location.state.coverFee;
    }

    if (this.state.feeBreakdown) {
      merged.feeBreakdown = this.state.feeBreakdown;
    } else if (
      merged.feeBreakdown === undefined &&
      this.props.location.state &&
      this.props.location.state.feeBreakdown
    ) {
      merged.feeBreakdown = this.props.location.state.feeBreakdown;
    }

    return merged;
  }

  render() {
    // Removed console.log from render method to prevent spam on every re-render
    // console.log(JSON.stringify(this.props.location.state) + " -204");
    // console.log(JSON.stringify(this.state) + " -137");

    if (
      this.props.location.state === null ||
      this.props.location.state === undefined
    ) {
      return <this.props.RenderFullPageLoading />;
    }

    // Removed duplicate onScroll definition - already defined and bound in constructor
    // this.onScroll is already bound and added to window scroll listener in componentDidMount

    if (this.state.paymentMethodId) {
      // Use transactionType passed from previous step
      let dataObj = this.attachFeeState({
        ...this.props.location.state,
        // transactionType should already be in this.props.location.state
      });
      dataObj.paymentMethod = this.state.paymentMethodId;
      dataObj.name = this.state.name;
      dataObj.email = this.state.email;
      dataObj.phone = this.state.phone;
      dataObj.brand = this.state.brand;
      dataObj.wallet = this.state.wallet;
      dataObj.last4 = this.state.last4;

      this.props.history.push({ pathname: "/Review", state: dataObj });
      return null;
    }
    
    const languageObj = this.props.languageObj;
    const organization = this.state.organization || {};
    const isFreePlanFlow =
      this.state.isFreePlanFlow || organization.currentPlan === "free";

    const freeHeaderHex = "#4A9FE9";
    const freeButtonHex = "#FFFFFF";
    const resolved = resolveThemeColors(organization.headerColor, organization.buttonColor);
    const defaultHeaderHex = `#${resolved.headerColor}`;
    const defaultButtonHex = `#${resolved.buttonColor}`;

    // Original org color for view/header/bg backgrounds
    const originalHeaderHex = organization.headerColor
      ? `#${organization.headerColor}`
      : "#000000";

    // For UI elements (buttons, toggles), use default blue for free plans
    const headerHex = isFreePlanFlow ? freeHeaderHex : defaultHeaderHex;
    const buttonHex = isFreePlanFlow ? freeButtonHex : defaultButtonHex;

    // For logo wrapper, use actual organization color even for free plans
    const logoHeaderHex = organization.orgHeaderColor
      ? `#${organization.orgHeaderColor}`
      : (isFreePlanFlow ? freeHeaderHex : defaultHeaderHex);

    const viewStyle = isFreePlanFlow
      ? {
        background:
            "linear-gradient(180deg, #eaf4ff 0%, #f7fbff 60%, #ffffff 100%)",
      }
      : { background: originalHeaderHex };

    const headerStyle = isFreePlanFlow ? undefined : { background: originalHeaderHex };
    const smallLogoStyle = isFreePlanFlow ? undefined : { background: originalHeaderHex };

    const viewClassName = isFreePlanFlow
      ? "SelectPayment-view SelectPayment-view--free"
      : "SelectPayment-view";
    const headerClassName = isFreePlanFlow
      ? "SelectPayment-header SelectPayment-header--free"
      : "SelectPayment-header";
    const stepsContainerClass = isFreePlanFlow
      ? "SelectPayment-stepsContainer SelectPayment-stepsContainer--free"
      : "SelectPayment-stepsContainer";
    const backContainerClass = isFreePlanFlow
      ? "SelectPayment-backContainer SelectPayment-backContainer--free"
      : "SelectPayment-backContainer";
    const stepTextClass = isFreePlanFlow
      ? "SelectPayment-stepTxt SelectPayment-stepTxt--free"
      : "SelectPayment-stepTxt";
    const indicatorClass = isFreePlanFlow
      ? "SelectPayment-indicator SelectPayment-indicator--free"
      : "SelectPayment-indicator";
    const mainClass = isFreePlanFlow
      ? "SelectPayment-main SelectPayment-main--free"
      : "SelectPayment-main";
    const bodyClass = isFreePlanFlow
      ? "SelectPayment-body SelectPayment-body--free"
      : "SelectPayment-body";
    const subTextClass = isFreePlanFlow
      ? "SelectPayment-subText SelectPayment-subText--free"
      : "SelectPayment-subText";
    const gatewayClass = isFreePlanFlow
      ? "SelectPayment-gateway SelectPayment-gateway--free"
      : "SelectPayment-gateway";
    const organizationLogoUrl =
      typeof organization.logo === "string" ? organization.logo : null;
    // const transactionType = this.props.location.state.transactionType || "donation";
    // const introDonationLabel =
    //   languageObj && languageObj.introDonationLabel
    //     ? languageObj.introDonationLabel
    //     : "You are making a donation to";
    // const introPaymentLabel =
    //   languageObj && languageObj.introPaymentLabel
    //     ? languageObj.introPaymentLabel
    //     : "You are making a payment to";
    // const baseIntroLabel =
    //   transactionType === "payment" ? introPaymentLabel : introDonationLabel;
    // const introText = organization.organizationName
    //   ? `${baseIntroLabel} ${organization.organizationName}`
    //   : baseIntroLabel;

    const RenderPaymentBtn = () => {
      return this.state.canMakePayment &&
        (this.state.organization.currentPlan === "premium" || this.state.organization.currentPlan === "pro") &&
        !isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_WALLET_KEY) &&
        !this.state.isRecurring &&
        !(this.props.isAuth || this.state.isAuth) ? (
          <PaymentRequestButtonElement
            className="PaymentRequestButton"
            options={{
              paymentRequest: this.state.paymentRequest,
              style: {
              // For more details on how to style the Payment Request Button, see:
              // https://stripe.com/docs/elements/payment-request-button#styling-the-element
                paymentRequestButton: {
                  theme: "dark",
                  height: "64px",
                },
              },
            }}
          />
        ) : null;
    };

    const RenderPayMeBtn = () => {
      // PayMe is now available for both authenticated and non-authenticated users
      if (isPaymentMethodHiddenOnProject(this.state.mission, 'payme')) return null;

      return (
        <div>
          {/* Credentials are no longer sent to the browser; the backend says
              whether they exist (CP-326). */}
          {organizationHasPayMeCredentials(this.state.organization) &&
            (this.state.organization.currentPlan === "premium" || this.state.organization.currentPlan === "pro") && (
            <div
              className="SelectPayment-paymeBtn"
              onClick={() => {
                let dataObj = this.attachFeeState({
                  ...this.props.location.state,
                  brand: "PayMe",
                  // Ensure transaction type is preserved
                  transactionType: this.props.location.state.transactionType || 'donation',
                });

                this.props.history.push({
                  pathname: "/Review",

                  state: dataObj,
                });
              }}
            ></div>
          )}
        </div>
      );
    };

    /* const onWechatBtnClick = (e) => {
      e.preventDefault();

      let dataObj = this.props.location.state;
      dataObj.paymentmethod = "";
      dataObj.brand = "WeChat";
      this.props.history.push({ pathname: "/Review", state: dataObj });
    }; */

    const RenderQrLink = () => {
      const qrLink = this.state.qrLink;
      if (!qrLink || !this.state.sourceData) return null;

      const nextBtnStyle = {
        background: headerHex,
        color: buttonHex,
        marginTop: "20px",
      };

      const clickFunc = () => {
        this.props.stripe
          .retrieveSource(this.state.sourceData)
          .then(() => {
            return null;
          });
      };

      return (
        <div>
          <div style={{ textAlign: "center" }}>
            <QRCodeCanvas value={qrLink} />
          </div>
          <div
            className="SelectPayment-nextBtn"
            style={nextBtnStyle}
            onClick={clickFunc}
          >
            Complete
          </div>
        </div>
      );
    };

    const RenderCard = () => {
      const allCards = this.state.cards;
      if (!allCards) return null;

      // Stored cards are cards: a project that hides card payment hides these
      // too, otherwise a returning donor still sees a way to pay by card.
      if (isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_CARD_KEY)) {
        return null;
      }

      const cardData = allCards.data;

      // Only show stored payments section if there are actually saved cards
      if (!Array.isArray(cardData) || cardData.length === 0) return null;

      const onCardClicked = (paymentMethod, brand, last4) => {
        // Use transactionType passed from previous step
        let dataObj = this.attachFeeState({
          ...this.props.location.state,
          // transactionType should already be in this.props.location.state
        });
        dataObj.paymentMethod = paymentMethod;
        dataObj.brand = brand;
        dataObj.last4 = last4;
        this.props.history.push({
          pathname: "/Review",
          state: dataObj,
        });
      };

      return (
        <div>
          <div className="SelectPayment-subText">
            {languageObj.storedPayments}
          </div>
          {cardData.map((item, idx) => (
            <CardItem key={idx} data={item} onCardClicked={onCardClicked} />
          ))}
        </div>
      );
    };

    /* const RenderWeChat = () => {
      if (!this.state.paymentMethod.wechatPay) return null;
      return (
        <div className="SelectPayment-wechat" onClick={onWechatBtnClick}></div>
      );
    }; */

    // Org setting narrowed by the project's own hidden list (CP-280), so a
    // project can drop a method from its picker without affecting the others.
    const checkPaymentMethods = (key, state) => {
      if (!state) return false;
      return isPaymentMethodAvailable(state.organization, state.mission, key);
    };

    // eslint-disable-next-line react/prop-types
    const RenderManualPaymentBtn = ({ brand, className, text, textColor }) => {
      const isEnabled = checkPaymentMethods(brand, this.state);
    
      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-button"
          data-testid={`selectpayment-manual-${brand}`}
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand,
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
          <div className="SelectPayment-buttonContainer">
            <div className= {`${className}`} />
            <div className= {`SelectPayment-buttonText ${textColor}`}>{text}</div>
          </div>
        </div>
      );
    };

    RenderManualPaymentBtn.propTypes = {
      brand: PropTypes.string.isRequired,
      className: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      textColor: PropTypes.string.isRequired
    };

    const manualMethodKeys = [
      'alipay_hk',
      'alipay_ch',
      'wechatpay_hk',
      'wechatpay_ch',
      'bocpay',
      'octopus',
      'bank_transfer',
      'check_writing',
      'cash',
      'paypal_manual'
    ];

    // Check if this is a recurring payment
    // Match the logic from PersonalDetails.isRecurringTransaction()
    const isRecurringFrequency = this.state.frequency && this.state.frequency !== "ONETIME";
    const isRecurringPayment = isRecurringFrequency && (
      (this.state.recurringOccurrences && Number(this.state.recurringOccurrences) > 0) ||
      this.state.isInfiniteRecurring === true
    );

    const hasManualOptions = manualMethodKeys.some((key) => checkPaymentMethods(key, this.state));
    const storedCards = this.state.cards && this.state.cards.data;
    const hasStoredCards = Array.isArray(storedCards) && storedCards.length > 0;
    const hasCardOption =
      this.state.charges_enabled !== false &&
      !isPaymentMethodHiddenOnProject(this.state.mission, PROJECT_CARD_KEY);
    // PayMe has two live configurations - a paymentMethodCode entry and the
    // older per-org credentials - so the project hide is applied once, over
    // both, rather than only inside checkPaymentMethods.
    const hasPayMeOption =
      organizationHasPayMe(this.state.organization) &&
      !isPaymentMethodHiddenOnProject(this.state.mission, 'payme');

    // For recurring payments, only allow credit cards (Stripe)
    const hasAnyPaymentOption = isRecurringPayment
      ? (hasCardOption || hasStoredCards)
      : (hasCardOption || hasStoredCards || hasManualOptions || hasPayMeOption);

    const RenderAlipayBtn = () => {
      const isEnabled = checkPaymentMethods("alipay_hk", this.state) || checkPaymentMethods("alipay_ch", this.state);
    
      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-alipay SelectPayment-button"
          data-testid="selectpayment-alipay-btn"
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand: "alipay",
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
        </div>
      );
    };
    
    const RenderWeChatPayBtn = () => {
      const isEnabled = checkPaymentMethods("wechatpay_hk", this.state) || checkPaymentMethods("wechatpay_ch", this.state);
    
      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-wechatPay SelectPayment-button"
          data-testid="selectpayment-wechatpay-btn"
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand: "wechatpay",
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
        </div>
      );
    };

    const RenderbocpayBtn = () => {
      const isEnabled = checkPaymentMethods("bocpay", this.state);
    
      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-bocPay SelectPayment-button"
          data-testid="selectpayment-bocpay-btn"
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand: "bocpay",
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
        </div>
      );
    };

    const RenderOctopusBtn = () => {
      const isEnabled = checkPaymentMethods("octopus", this.state);
    
      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-octopus SelectPayment-button"
          data-testid="selectpayment-octopus-btn"
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand: "octopus",
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
        </div>
      );
    };

    const RenderFPSBtn = () => {
      const isEnabled = checkPaymentMethods("fps", this.state);

      if (!isEnabled) return null;
    
      return (
        <div
          className="SelectPayment-FPS SelectPayment-button"
          data-testid="selectpayment-fps-btn"
          onClick={() => {
            let dataObj = this.attachFeeState({
              ...this.props.location.state,
              brand: "fps",
              // Ensure transaction type is preserved
              transactionType: this.props.location.state.transactionType || 'donation',
            });
    
            this.props.history.push({
              pathname: "/ManualPayments",
              state: dataObj,
            });
          }}
        >
        </div>
      );
    };

    // Create proper logo style based on the type of logo property
    const createLogoStyle = (logo) => {
      if (typeof logo === 'string') {
        // If logo is a URL string, create a backgroundImage style
        return { 
          backgroundImage: `url('${logo}')`, 
          backgroundSize: 'contain', 
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center'
        };
      } else if (typeof logo === 'object') {
        // If logo is already a style object, use it directly
        return logo;
      }
      // Default empty object if logo is undefined or another type
      return {};
    };
    
    const logoStyle = createLogoStyle(organization.logo);
    const freeHeaderLogoStyle = {
      backgroundColor: logoHeaderHex, // Use organization's actual color for logo wrapper
      padding: '12px 20px',
      borderRadius: '16px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      maxWidth: '280px',
      width: '100%'
    };

    const renderBackButton = ({ hideText = false } = {}) => (
      <div className={backContainerClass} data-testid="selectpayment-back-btn" onClick={this.onBackBtnClicked}>
        <div className="SelectPayment-back">
          <div className="SelectPayment-backBtn" />
          {!hideText ? (
            <div className="SelectPayment-backText">{languageObj.backText}</div>
          ) : (
            <span className="SelectPayment-backTextSrOnly">{languageObj.backText}</span>
          )}
        </div>
      </div>
    );

    const renderSteps = ({ showBack = true } = {}) => (
      <div className={stepsContainerClass}>
        {showBack ? renderBackButton() : null}
        <div className="SelectPayment-stepsContent">
          <div className={stepTextClass} data-testid="selectpayment-step-text">
            {this.state.mission.status === "unlisted"
              ? (languageObj.step3of4 || "Step 3 of 4")
              : languageObj.stepTxt}
          </div>
          <div className={indicatorClass}>
            {this.state.mission.status === "unlisted" ? (
              <>
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleEmpty" />
              </>
            ) : (
              <>
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleSelected SelectPayment-marginRight" />
                <div className="SelectPayment-circle SelectPayment-circleEmpty" />
              </>
            )}
          </div>
        </div>

        {!isFreePlanFlow && this.state.isScrolling ? (
          <div
            className="SelectPayment-smallLogoContainer SelectPayment-fadeIn"
            style={smallLogoStyle}
          >
            <div className="SelectPayment-logoS" style={logoStyle}></div>
          </div>
        ) : null}
      </div>
    );

    const renderFreeBodyHeader = () => {
      if (!isFreePlanFlow) return null;

      return (
        <div className="SelectPayment-freeHeaderLogoWrapper" style={{ marginBottom: '24px' }}>
          {organizationLogoUrl ? (
            <div className="SelectPayment-freeHeaderLogoInner" style={freeHeaderLogoStyle}>
              <img
                className="SelectPayment-freeHeaderLogo"
                src={organizationLogoUrl}
                alt={organization.organizationName}
              />
            </div>
          ) : (
            <div
              className="SelectPayment-freeHeaderLogoFallback"
              style={{
                ...freeHeaderLogoStyle,
                color: '#2c3a4b'
              }}
            >
              {organization.organizationName}
            </div>
          )}
        </div>
      );
    };

    const renderHeader = () => {
      if (isFreePlanFlow) {
        return (
          <div className={headerClassName}>
            <div className="SelectPayment-freeBrand">
              <span className="SelectPayment-freeBrandPowered">Powered by</span>
              <img
                src={PLATFORM === "plug" ? plugLogo : checkplusLogo}
                alt={PLATFORM === "plug" ? "Plug" : "CheckPlus for Nonprofits"}
                className="SelectPayment-freeBrandLogoImage"
              />
            </div>
            <div className="SelectPayment-freeHeaderStepsWrapper">
              {renderSteps({ showBack: false })}
            </div>
            <div className="SelectPayment-freeHeaderNote">
              {languageObj.secureCheckout || "Secure Checkout"}
            </div>
          </div>
        );
      }

      return (
        <div className={headerClassName} style={headerStyle}>
          <div className="SelectPayment-logo" style={logoStyle}>
            {!organization.logo ? "L O G O" : ""}
          </div>
        </div>
      );
    };

    // Show loading indicator when fetching organization data
    if (this.state.fetchingOrganizationData) {
      return (
        <div className={viewClassName} style={viewStyle}>
          {renderHeader()}
          <div className="SelectPayment-main" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div style={{ fontSize: '18px', marginBottom: '20px' }}>
              Loading payment methods...
            </div>
            <div style={{ 
              width: '50px', 
              height: '50px', 
              border: '5px solid #f3f3f3', 
              borderTop: `5px solid ${headerHex}`,
              borderRadius: '50%',
              animation: 'spin 2s linear infinite',
              margin: '0 auto'
            }} />
            <style>
              {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
            </style>
          </div>
        </div>
      );
    }
    
    // Show error message if fetching failed
    if (this.state.fetchError) {
      return (
        <div className={viewClassName} style={viewStyle}>
          {renderHeader()}
          <div className="SelectPayment-main" style={{ textAlign: 'center', padding: '50px 20px' }}>
            <div
              data-testid="selectpayment-fetch-error"
              style={{
                color: 'red',
                fontSize: '18px',
                marginBottom: '20px',
                fontWeight: 'bold'
              }}
            >
              Error Loading Payment Methods
            </div>
            <div style={{ marginBottom: '20px' }}>
              {this.state.fetchError}
            </div>
            <div 
              style={{
                background: headerHex,
                color: buttonHex,
                padding: '10px 20px',
                borderRadius: '5px',
                display: 'inline-block',
                cursor: 'pointer'
              }}
              onClick={this.onBackBtnClicked}
            >
              Go Back
            </div>
          </div>
        </div>
      );
    }
    
    return (
      <div className={viewClassName} style={viewStyle}>
        <Helmet>
          <title>{organization.organizationName}</title>
        </Helmet>
        {renderHeader()}

        <div className={mainClass}>
          {!isFreePlanFlow && renderSteps()}

          <div className={bodyClass}>
            {isFreePlanFlow && renderBackButton()}
            {renderFreeBodyHeader()}
            <div className={subTextClass}>{languageObj.subText}</div>

            {/* Recurring payment info message */}
            {isRecurringPayment && (
              <div style={{
                backgroundColor: '#E3F2FD',
                border: '1px solid #2196F3',
                borderRadius: '8px',
                padding: '12px 16px',
                margin: '16px 0',
                fontSize: '14px'
              }}>
                <div style={{ color: '#1976D2', fontWeight: '500', marginBottom: '4px' }}>
                  <span role="img" aria-label="recurring">🔁</span> Recurring Payment
                </div>
                <div style={{ color: '#424242', fontSize: '13px' }}>
                  For recurring payments, only credit/debit cards are accepted. Other payment methods are not available for automatic recurring transactions.
                </div>
              </div>
            )}

            {!hasAnyPaymentOption && (
              <div className="SelectPayment-noPaymentWarning" data-testid="selectpayment-no-payment-warning">
                <div className="SelectPayment-warningIcn" />
                <div className="SelectPayment-warningTxt">
                  {languageObj.noPaymentWarning}
                </div>
              </div>
            )}

            <div className={gatewayClass}>

              {hasCardOption ? (
                <div
                  className="SelectPayment-CC"
                  data-testid="selectpayment-card-option"
                  onClick={this.onCardBtnClicked}
                />
              ) : null}

              {!isRecurringPayment && <RenderPayMeBtn />}

              {/* }
                <div className="SelectPayment-paypal SelectPayment-button" />
                <div className="SelectPayment-TapAndGo SelectPayment-button" />
              */}

              <RenderCard />
              <RenderQrLink />
              <RenderPaymentBtn />


              {!isRecurringPayment && this.state.organization && this.state.organization.currentPlan === "premium" && (
                <>
                  <div className="SelectPayment-subText">{languageObj.manualPaymentsLabel || "Manual Payments"}</div>
                  <RenderAlipayBtn />
                  <RenderWeChatPayBtn />
                  <RenderbocpayBtn />
                  <RenderOctopusBtn />
                  <RenderFPSBtn />
                  <RenderManualPaymentBtn brand="bank_transfer" className="SelectPayment-bankTransfer" textColor="SelectPayment-bankTransfer-color" text={languageObj.bankTransfer || "Bank Transfer"} />
                  <RenderManualPaymentBtn brand="check_writing" className="SelectPayment-checkWriting" textColor="SelectPayment-checkWriting-color" text={languageObj.checkWriting || "Cheque"} />
                  {/* <RenderManualPaymentBtn brand="cash" className="SelectPayment-cash" textColor="SelectPayment-cash-color" text="Cash" /> */}
                  <RenderManualPaymentBtn brand="paypal_manual" className="SelectPayment-paypalManual" textColor="SelectPayment-paypalManual-color" text="" />
                </>
              )}

            </div>
          </div>
        </div>
      </div>
    );
  }
}

/* const checkPaymentMethods = (key, state) => {
  // Check if state and organization exist
  if (!state || !state.organization) {
    // console.log('State or organization is undefined for payment method: ' + key);
    return false;
  }
  
  // Check if paymentMethodCode exists
  const paymentMethods = state.organization.paymentMethodCode;
  if (!paymentMethods) {
    // console.log('Payment methods not defined for organization when checking: ' + key);
    return false;
  }
  
  // console.log('Checking payment method: ' + key + ' in paymentMethodCode: ' + JSON.stringify(paymentMethods));

  // Check if the specific payment method exists
  if (!paymentMethods[key]) {
    // console.log('Payment method ' + key + ' not found in paymentMethodCode');
    return false;
  }
  
  // Check if the payment method has a code and is enabled
  if (paymentMethods[key].code !== '' && paymentMethods[key].enabled) {
    // console.log('Payment method enabled: ' + key + ' with code: ' + paymentMethods[key].code);
    return true;
  }
  
  // Log specific reason for disabled payment method
  if (paymentMethods[key].code === '') {
    // console.log('Payment method ' + key + ' has empty code');
  }
  
  if (!paymentMethods[key].enabled) {
    // console.log('Payment method ' + key + ' is not enabled');
  }
  
  return false;
}; */

SelectPayment.propTypes = {
  cards: PropTypes.array,
  stripe: PropTypes.object, // Not required - Stripe loads asynchronously in React 18
  languageObj: PropTypes.object,
  isAuth: PropTypes.bool,
};

// Wrapper component to use Stripe hooks with class component
function SelectPaymentWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();

  return <SelectPayment {...props} stripe={stripe} elements={elements} />;
}

export default SelectPaymentWithStripe;
